import React, { useState } from "react";
import Icon from "components/AppIcon";

const properties = [
  {
    id: 1, address: "5412 Farnam Street", city: "Omaha, NE",
    price: 285000, monthlyRent: 1950, capRate: 6.8, cashOnCash: 7.2,
    roi5yr: 8.4, grm: 12.2, noi: 19380, dscr: 1.42, irr: 11.2,
    appreciation: 4.2, vacancy: 4.5, expenseRatio: 32, riskScore: 2,
  },
  {
    id: 2, address: "3218 Dodge Street", city: "Dundee, Omaha NE",
    price: 248000, monthlyRent: 1720, capRate: 6.2, cashOnCash: 6.8,
    roi5yr: 7.9, grm: 12.0, noi: 15376, dscr: 1.31, irr: 10.4,
    appreciation: 5.1, vacancy: 5.2, expenseRatio: 38, riskScore: 3,
  },
  {
    id: 3, address: "1842 S 48th Street", city: "Aksarben, Omaha NE",
    price: 215000, monthlyRent: 1480, capRate: 5.9, cashOnCash: 6.1,
    roi5yr: 7.1, grm: 12.1, noi: 12685, dscr: 1.28, irr: 9.8,
    appreciation: 3.8, vacancy: 6.1, expenseRatio: 36, riskScore: 2,
  },
  {
    id: 4, address: "14820 Arbor Street", city: "West Omaha, NE",
    price: 365000, monthlyRent: 2200, capRate: 5.4, cashOnCash: 5.9,
    roi5yr: 6.8, grm: 13.8, noi: 19710, dscr: 1.19, irr: 9.1,
    appreciation: 6.2, vacancy: 4.8, expenseRatio: 34, riskScore: 4,
  },
  {
    id: 5, address: "602 Galvin Road S", city: "Bellevue, NE",
    price: 198000, monthlyRent: 1350, capRate: 4.8, cashOnCash: 5.1,
    roi5yr: 6.2, grm: 12.2, noi: 9504, dscr: 1.12, irr: 8.6,
    appreciation: 5.8, vacancy: 3.9, expenseRatio: 31, riskScore: 3,
  },
];

const columns = [
  { key: "address", label: "Property", sortable: false },
  { key: "price", label: "Price", sortable: true, format: (v) => `$${(v / 1000)?.toFixed(0)}K` },
  { key: "monthlyRent", label: "Mo. Rent", sortable: true, format: (v) => `$${v?.toLocaleString()}` },
  { key: "capRate", label: "Cap Rate", sortable: true, format: (v) => `${v}%` },
  { key: "cashOnCash", label: "CoC Return", sortable: true, format: (v) => `${v}%` },
  { key: "roi5yr", label: "5yr ROI", sortable: true, format: (v) => `${v}%` },
  { key: "grm", label: "GRM", sortable: true, format: (v) => `${v}x` },
  { key: "noi", label: "NOI", sortable: true, format: (v) => `$${v?.toLocaleString()}` },
  { key: "dscr", label: "DSCR", sortable: true, format: (v) => v?.toFixed(2) },
  { key: "irr", label: "IRR", sortable: true, format: (v) => `${v}%` },
  { key: "appreciation", label: "Apprec.", sortable: true, format: (v) => `${v}%` },
  { key: "riskScore", label: "Risk", sortable: true, format: (v) => v },
];

const riskColors = { 1: "text-success", 2: "text-success", 3: "text-warning", 4: "text-accent", 5: "text-destructive" };
const riskLabels = { 1: "Very Low", 2: "Low", 3: "Medium", 4: "High", 5: "Very High" };

export default function ComparisonMatrix() {
  const [sortKey, setSortKey] = useState("roi5yr");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("desc"); }
  };

  const sorted = [...properties]?.sort((a, b) => {
    const av = a?.[sortKey], bv = b?.[sortKey];
    if (typeof av === "string") return sortDir === "asc" ? av?.localeCompare(bv) : bv?.localeCompare(av);
    return sortDir === "asc" ? av - bv : bv - av;
  });

  const best = (key) => {
    const vals = properties?.map((p) => p?.[key]);
    return key === "riskScore" || key === "price" || key === "grm" || key === "vacancy" || key === "expenseRatio"
      ? Math.min(...vals)
      : Math.max(...vals);
  };

  return (
    <div className="card-base flex flex-col gap-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="font-heading font-semibold text-base text-foreground">
            Investment Comparison Matrix
          </h3>
          <p className="text-xs font-caption text-muted-foreground mt-0.5">
            Sortable metrics across all tracked properties
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs font-caption text-muted-foreground">
          <span className="w-2.5 h-2.5 rounded-sm bg-success/20 border border-success inline-block" />
          Best value
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-muted border-b border-border">
              {columns?.map((col) => (
                <th
                  key={col?.key}
                  onClick={() => col?.sortable && handleSort(col?.key)}
                  className={`px-3 py-3 text-left text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${
                    col?.sortable ? "cursor-pointer hover:text-foreground transition-standard select-none" : ""
                  }`}
                >
                  <span className="flex items-center gap-1">
                    {col?.label}
                    {col?.sortable && sortKey === col?.key && (
                      <Icon
                        name={sortDir === "asc" ? "ChevronUp" : "ChevronDown"}
                        size={12}
                        color="var(--color-primary)"
                      />
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted?.map((p, idx) => (
              <tr
                key={p?.id}
                className={`border-b border-border transition-standard hover:bg-muted/50 ${
                  idx % 2 === 0 ? "bg-background" : "bg-muted/20"
                }`}
              >
                {columns?.map((col) => {
                  const val = p?.[col?.key];
                  const isBest = col?.sortable && val === best(col?.key);
                  const isRisk = col?.key === "riskScore";
                  return (
                    <td
                      key={col?.key}
                      className={`px-3 py-3 whitespace-nowrap font-caption ${
                        isBest ? "bg-success/10" : ""
                      }`}
                    >
                      {col?.key === "address" ? (
                        <div>
                          <p className="text-sm font-heading font-semibold text-foreground truncate max-w-[160px]">
                            {p?.address}
                          </p>
                          <p className="text-xs text-muted-foreground">{p?.city}</p>
                        </div>
                      ) : isRisk ? (
                        <span className={`font-semibold text-xs ${riskColors?.[val]}`}>
                          {riskLabels?.[val]}
                        </span>
                      ) : (
                        <span className={`font-mono-data text-sm ${isBest ? "text-success font-bold" : "text-foreground"}`}>
                          {col?.format ? col?.format(val) : val}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}